import { useTranslation } from 'react-i18next'
import { useBookingEngineContext } from '../../../Contexts/EngineDataContext'
import { useEffect, useRef, useState } from 'react'
import {
    BookingData,
    Cart,
    EngineWidget,
    FinderIcon,
    HomeMinimalist,
    NoData,
    Pagination
} from '../../../components'
import { Step as EngineSteps } from '@beds2b-group/reusable-components'
import {
    fetchHotelAvailability,
    fetchLocationAvailability,
    fetchUserHistory,
    GetCountries,
    GetPaymentsMethods
} from '../../../api/repositoryEP'

import './BookingEnginePage.scss'
import LoadingPage from '../../Commons/LoadingPage/LoadingPage'
import { HistoryItem } from '@beds2b-group/reusable-components/dist/types'

import '../../../components/booking-engine/HotelModalWrapper'
import '../../../components/booking-engine/RoomModalWrapper'
import '../../../components/booking-engine/RoomRateModalWrapper'

import { useModal } from '@gluedigital/modal'
import { AvailabilityRequest } from '../../../models/availability/AvailabilityRequest'

import {
    EngineDestinationTypeHotel,
    EngineDestinationTypeLocation,
    emptyLocationAvailabilityResponse,
    emptyHotelAvailabilityResponse
} from '../../../utils/constants'
import {
    LocationAvailability,
    LocationAvailabilityResponse
} from '../../../models/availability/LocationsAvailability'
import {
    HotelAvailability,
    HotelAvailabilityResponse,
    RoomAvailability
} from '../../../models/availability/HotelAvailability'
import { LocationToAppAvailability } from '../../../utils/LocationToAppAvailability'
import { HotelsToAppAvailability } from '../../../utils/HotelsToAppAvailability'
import { getHotelErrorMessage } from '../../../utils/availabilityErrors'
import { Button, Col, Collapse, Modal, Row } from 'antd'
import hotelUtils from '../../../mocks/hotels'
import { useCart } from '../../../Contexts/CartContexts'
import { useLocation, useNavigate } from 'react-router-dom'
import { GetFormattedUrl, GetLanguageInUrl, IsLaguagePresentInUrl } from '../../../utils/urls'
import { useAppConfig } from '../../../Contexts/AppConfigContext'
import { getAvailableHotels, searchCoincidencesForWidget } from '../../../utils/utils'
import { useSessionData } from '../../../Contexts/SessionDataContext'
import { useCurrencyData } from '../../../Contexts/CurrencyContext'
import { PaymentMethodData } from '../../../models/PaymentsMethodsData'
import { GetAppLanguage } from '../../../RoutesApp'
import dayjs from 'dayjs'
import {
    Hotel,
    HotelStaticData,
    Price,
    RoomBedType,
    Service
} from '../../../models/availability/Commons'
import { GenericGlobalKeyValuePair } from '../../../models/CustomField'

const BookingEnginePage = (): JSX.Element => {
    const [bookingId, setBookingId] = useState<string>('')
    const navigate = useNavigate()
    const location = useLocation()
    const modal = useModal()
    const cartContext = useCart()
    const appConfig = useAppConfig()
    const sessionData = useSessionData()
    const bookingEngineContext = useBookingEngineContext()
    const [cleanForm, setCleanForm] = useState<boolean>(false)
    const [availabilityType, setAvailabilityType] = useState<number>(EngineDestinationTypeLocation) // [hotel, room
    const [isGettingAvailability, setIsGettingAvailability] = useState<boolean>(false)
    const [locationAvailability, setLocationAvailability] = useState<LocationAvailabilityResponse>(
        emptyLocationAvailabilityResponse
    )
    const [locationAvailabilityFiltered, setLocationAvailabilityFiltered] =
        useState<LocationAvailabilityResponse>(emptyLocationAvailabilityResponse)
    const [originalLocationAvailability, setOriginalLocationAvailability] =
        useState<LocationAvailabilityResponse>(emptyLocationAvailabilityResponse)
    const [hotelAvailability, setHotelAvailability] = useState<HotelAvailabilityResponse>(
        emptyHotelAvailabilityResponse
    )
    const [originalHotelAvailability, setOriginalHotelAvailability] =
        useState<HotelAvailabilityResponse>(emptyHotelAvailabilityResponse)
    const [showNoDispo, setShowNoDispo] = useState<boolean>(false)
    // Mensaje de NoData cuando el hotel viene con errorCode a nivel de hotel ('' = mensaje genérico)
    const [hotelErrorMessage, setHotelErrorMessage] = useState<string>('')
    const [actualStepRoomSelector, setActualStepRoomSelector] = useState<number>(0)
    const [actualStepBooking, setActualStepBooking] = useState<number>(1)
    const [paymentsMethods, setPaymentsMethods] = useState<PaymentMethodData[]>([])
    const [userHistory, setUserHistory] = useState<Array<HistoryItem>>([])
    const client = appConfig.config.web;

    const redirectParam = location.state?.redirect
    const { t } = useTranslation()

    const [countries, setCountries] = useState<GenericGlobalKeyValuePair<string, string>[]>([])
    useEffect(() => {
        GetCountries().then((res) => {
            if (res?.type == 1 && res.data) {
                setCountries(res.data as GenericGlobalKeyValuePair<string, string>[])
            }
        })
    }, [])

    const [isBookingFormValid, setIsBookingFormValid] = useState<boolean>(false);


    useEffect(() => {
        if (!redirectParam) {
            bookingEngineContext.clearContext()
            setRoomsAvailability([])
            setCleanForm(true)

            setEngineReady(true)   // listo pero limpio
            return
        }
        const last = sessionStorage.getItem('lastOccupancy')

        if (last) {
            try {
                const rooms = JSON.parse(last)
                console.log(" la room en el useEffect es ", rooms)

                // Recuperamos también las fechas
                const dateFrom = sessionStorage.getItem('lastFrom')
                const dateTo = sessionStorage.getItem('lastTo')

                bookingEngineContext.updateContext({
                    ...bookingEngineContext,
                    rooms: rooms,
                    dates: {
                        from: dateFrom ? dayjs(dateFrom) : null,
                        to: dateTo ? dayjs(dateTo) : null
                    }
                })

            } catch (e) {
                console.error("Error restaurando lastOccupancy", e)
            }
            setEngineReady(true) // listo con datos restaurados
        }
    }, [])

    const bookingDataFormRef = useRef<any>(null) // Crear la referencia para BookingDataForm

    // Función para hacer scroll al botón de pago
    const scrollToPayButton = () => {
        if (bookingDataFormRef.current?.scrollToButton) {
            bookingDataFormRef.current.scrollToButton() // Llamamos a la función scrollToButton de BookingDataForm
        } else {
            console.error(' No se pudo acceder a la función scrollToButton')
        }
    }

    const onChangeStep = (step: number) => {
        //Permitimos el cambio del 3 al 2 y del 2 al 3 siempre que:
        //  - El paso 2 esté completo: Se hayan seleccionado todas las habitaciones
        //  - Del paso 3 al 2 se puede pasar siempre.
        // No se permiten otros cambios de paso.
        var newStep = step + 1

        if (actualStepBooking === 3 && newStep === 2) {
            setActualStepBooking(newStep)
            setActualStepRoomSelector(1)
        } else if (actualStepBooking === 2 && newStep === 3) {
            if (cartContext.data.rooms.length == bookingEngineContext.rooms.length) {
                setActualStepBooking(newStep)
                setActualStepRoomSelector(bookingEngineContext.rooms.length + 1)
            }
        }
    }

    const getUserHistory = () => {
        fetchUserHistory().then((response) => {
            if (response) {
                setUserHistory(response.data)
            } else {
                setUserHistory([])
            }
        })
    }

    const [searchCoincidences, setSearchCoincidences] = useState<Array<any>>([])
    const getSearchCoincidences = (v: string) => {
        if (v.length < 3) return setSearchCoincidences([])

        var coincidences = searchCoincidencesForWidget(appConfig.config.hotels, v)

        setSearchCoincidences(coincidences)
    }

    const getAvailability = () => {
        setIsGettingAvailability(true)
        setHotelErrorMessage('') // cada búsqueda empieza sin mensaje de error de hotel

        if (bookingEngineContext.dates.from == null || bookingEngineContext.dates.to == null) return

        var actualLanguage = IsLaguagePresentInUrl()
            ? GetLanguageInUrl().substring(1)
            : appConfig.config.availableLanguages.find((l) => l.isDefault)?.shortCode
        if (actualLanguage) {
            //sessionStorage.setItem('lastFrom', dayjs(dateFromFromUrl).format("YYYY-MM-DDTHH:mm:ss.sssZ"))

            var availabilityData: AvailabilityRequest = {
                
                LanguageIsoCode: actualLanguage,
                DestinationType: bookingEngineContext.searchType,
                DestinationCodes: [bookingEngineContext.searchCode],
                AccommodationDates: {
                    Format: appConfig.config.dateFormat,
                    From: bookingEngineContext.dates.from.format('YYYY-MM-DDT00:00:00'),
                    To: bookingEngineContext.dates.to.format('YYYY-MM-DDT00:00:00')
                },
                StaticDataDetailLevel: 2,
                PromotionalCode: bookingEngineContext.promotionalCode,
                RoomConfiguration: [],
                IncludeAvailabilityWithoutquota: false,
                IncludeAlternatives: true,
                IncludeCrossSellingProducts: false,
                MarketCode: sessionData.userData.market,
                DestinationName: sessionStorage.getItem('lastLabel') || bookingEngineContext.label,
                commissionConfigurationId: sessionData.userData.commissionConfigurationSelected || null,
                commissionConfigurationIsBase: appConfig.config.userCommissionConfiguration?.find(c => c.id === sessionData.userData.commissionConfigurationSelected)?.isBase ?? false
            }

            bookingEngineContext.rooms.forEach((room) => {
                availabilityData.RoomConfiguration.push({
                    RoomCode: '',
                    Occupancy: {
                        Adults: {
                            // La edad debe informarse al servicio Rellenamos le nodo ages con un array que contenga room.persons.adults.quantity veces la edad 24
                            Ages: Array(room.persons.adults.quantity).fill(24),
                            Number: room.persons.adults.quantity
                        },
                        Teenagers: {
                            Ages: [],
                            Number: 0
                        },
                        Babies: {
                            Ages: [],
                            Number: 0
                        },
                        Children: {
                            Ages: Object.values(room.persons.kids.ages),
                            Number: room.persons.kids.quantity
                        }
                    },
                    MealCode: []
                })
            })

            //Vamos al siguiente paso: Selección.
            setActualStepBooking(2)

            if (bookingEngineContext.searchType === EngineDestinationTypeLocation) {
                setShowNoDispo(false)
                setRoomsAvailability([])
                setRoomsAvailabilityFiltered([])
                setLocationAvailability(emptyLocationAvailabilityResponse)
                setAvailabilityType(EngineDestinationTypeLocation)
                bookingEngineContext.updateContext({
                    ...bookingEngineContext,
                    filters: {
                        priceRange: [null, null],
                        sizeRoomRange: [null, null],
                        regimens: [],
                        services: [],
                        preferenceBed: '',
                        freeCancel: ''
                    }
                })

                setActualStepRoomSelector(0)
                fetchLocationAvailability(availabilityData)
                    .then((response) => {
                        if (response != null && response.data != null) {
                            setLocationAvailability(response.data)
                            setOriginalLocationAvailability(response.data)
                            if (
                                response.data?.locationAvailabilities == null ||
                                (Array.isArray(response?.data?.locationAvailabilities) &&
                                    response.data.locationAvailabilities.length === 0)
                            ) {
                                setShowNoDispo(true)
                            }
                        } else {
                            setShowNoDispo(true)
                        }
                    })
                    .finally(() => {
                        setIsGettingAvailability(false)
                        // La API guarda la búsqueda en el historial: se recarga para que aparezca en el widget
                        getUserHistory()
                    })
            } else {
                //Mostramos la paginación de las habitaciones del hotel
                setShowNoDispo(false)
                setLocationAvailability(emptyLocationAvailabilityResponse)
                setOriginalLocationAvailability(emptyLocationAvailabilityResponse)
                setAvailabilityType(EngineDestinationTypeHotel)
                setActualStepRoomSelector(1)

                /**
                 * Miramos a ver si en session storage hay datos para la simulación
                 */
                const url = sessionStorage.getItem('integration-simulation-url')
                const username = sessionStorage.getItem('integration-simulation-username')
                const password = sessionStorage.getItem('integration-simulation-password')
                const market = sessionStorage.getItem('integration-simulation-market')
                availabilityData.simulationIntegrationData = {
                    url: url,
                    username: username,
                    password: password,
                    market: market
                }

                fetchHotelAvailability(availabilityData)
                    .then((response) => {
                        if (
                            response &&
                            response.data &&
                            response.data.hotelAvailabilities &&
                            response.data.hotelAvailabilities.length > 0 &&
                            response.data.hotelAvailabilities?.some(
                                (hotel: { hotelCode: string; roomAvailabilities: any }) =>
                                    hotel.hotelCode !== '-1' && hotel.roomAvailabilities !== null
                            )
                        ) {
                            setShowNoDispo(false)
                            const hotelData = response.data.hotels?.[0] ?? null

                            cartContext.setHotelData({
                                address: hotelData?.address?.streetName ?? t('cart-context.address-not-present'),
                                code: sessionStorage.getItem('lastSearchCode') || bookingEngineContext.searchCode,
                                name: hotelData?.name ?? (hotelData as any)?.Name ?? t('cart-context.hotel-name-not-present'),
                                email: hotelData?.contactData?.Email ?? t('cart-context.email-not-present'),
                                mainImage:
                                    hotelData?.images && hotelData?.images.length > 0
                                        ? hotelData?.images[0].url
                                        : (response.data.hotels?.[0]?.hotelStaticData?.[0]?.images
                                            ?.find((i: any) => i.isMainImage)?.url ??
                                            t('cart-context.image-not-present')
                                        ),
                                otherInfo: [],
                                personOfContact:
                                    hotelData?.contactData?.ContactPerson ??
                                    t('cart-context.contact-person-not-present'),
                                phone: hotelData?.contactData
                                    ? `(${hotelData.contactData?.Phone?.Prefix ?? ''}) ${hotelData.contactData?.Phone?.Number ?? ''}`
                                    : t('cart-context.phone-not-present'),
                                website: hotelData?.contactData?.WebSite ?? t('cart-context.website-not-present'),
                                longitude: hotelData?.address?.longitude?.toString() ?? '0',
                                latitude: hotelData?.address?.latitude?.toString() ?? '0'
                            })

                            cartContext.updateHashCart(response.data.availabilityRequestId)
                            setHotelAvailability(response.data)
                            //     console.log(response.data.hotelAvailabilities.roomAvailabilities)
                            setOriginalHotelAvailability(response.data)

                            setTimeout(onSetDefaultLabel, 500)
                        } else {
                            // Error a nivel de hotel (hotelAvailabilities[i].errorCode, sin lista de habitaciones):
                            // NoData muestra el mensaje específico del código en lugar del genérico ('' = genérico)
                            const hotelErrorCode: string | undefined = response?.data?.hotelAvailabilities?.find(
                                (hotel: HotelAvailability) => !!hotel?.errorCode
                            )?.errorCode
                            setHotelErrorMessage(hotelErrorCode ? getHotelErrorMessage(hotelErrorCode) : '')
                            setShowNoDispo(true)
                            cartContext.clearCart()
                            setTimeout(onSetDefaultLabel, 500)
                            setRoomsAvailability([])
                            setHotelAvailability(emptyHotelAvailabilityResponse)
                            // También el original: al quitar filtros se restaura desde aquí y no debe volver el hotel anterior
                            setOriginalHotelAvailability(emptyHotelAvailabilityResponse)
                        }
                    })
                    .finally(() => {
                        setIsGettingAvailability(false)
                        // La API guarda la búsqueda en el historial: se recarga para que aparezca en el widget
                        getUserHistory()
                    })
            }
        }
    }

    const [roomSteps, setRoomSteps] = useState<any[]>([])
    const getRoomSteps = (): void => {
        console.log("estamos en el egetRoomsSteps", bookingEngineContext.rooms)
        const roomSteps: any[] = []
        bookingEngineContext.rooms.forEach((room) => {
            roomSteps.push({
                adults: room.persons.adults.quantity,
                childs: room.persons.kids.quantity
            })
        })

        setRoomSteps(roomSteps)
    }

    const [availableDestinations, setAvailableDestinations] = useState<Array<any>>([])
    const getAvilableDestinations = () => {
        setAvailableDestinations(getAvailableHotels(appConfig.config.hotels))
    }

    const onSelectRoom = (roomData: any, mealData: any, rateData: any) => {
        var currency = mealData.isPvp ? mealData.pvp.currency : mealData.net.currency

        cartContext.addRoomToCart({
            roomIndex: actualStepRoomSelector,
            mainImage: roomData.images[0] ?? '',
            name: roomData.name,
            code: roomData.code,
            isNoRefundable: rateData.isNoRefundable,
            price: {
                currency: currency,
                pvp: mealData.pvp?.amount,
                commission: mealData.commission?.amount,
                isPvp: mealData.isPvp,
                net: mealData.net?.amount
            },
            occupancy: {
                adults: bookingEngineContext.rooms[actualStepRoomSelector - 1].persons.adults.quantity,
                childs: bookingEngineContext.rooms[actualStepRoomSelector - 1].persons.kids.quantity,
                childsAges: bookingEngineContext.rooms[actualStepRoomSelector - 1].persons.kids.ages
            },
            rateInfo: {
                isNoRefundable: rateData.isNoRefundable,
                name: rateData.name,
                code: rateData.code,
                isOffer: rateData.isOffer,
                cancelPolicies: rateData.cancelPolicies
            },
            mealInfo: {
                code: mealData.code,
                commission: mealData.commission,
                discount: mealData.discount,
                isPvp: mealData.isPvp,
                name: mealData.name,
                net: mealData.net,
                pvp: mealData.pvp,
                ratekey: mealData.rateKey
            }
        })

        // Go next step
        setActualStepRoomSelector(cartContext.whatIsTheNextRoomIndex())
    }

    const onRemoveRoomFromCart = (roomIndex: number, roomCode: string) => {
        setActualStepRoomSelector(roomIndex)
        setActualStepBooking(2)
    }

    const [isSimulateBookingPaymentOpened, setIsSimulateBookingPaymentOpened] =
        useState<boolean>(false)
    const toogleSimulateBookingPayment = () => {
        setIsSimulateBookingPaymentOpened(!isSimulateBookingPaymentOpened)
    }

    const [isCartVisible, setIsCartVisible] = useState<boolean>(false)

    const currencyContext = useCurrencyData()

    let selectedRoomsByIndex: Record<number, string> = {}
    cartContext.data.rooms.forEach((room) => {
        selectedRoomsByIndex[room.roomIndex] = room.code
    })

    const { currencyData, doExchangeCurrency } = useCurrencyData()
    const userCurrency = currencyData.currencySelectedISO
    const language = GetAppLanguage()

    const [roomsAvailability, setRoomsAvailability] = useState<JSX.Element[]>([])
    const [roomsAvailabilityFiltered, setRoomsAvailabilityFiltered] = useState<JSX.Element[]>([])
    const [originalCurrency, setOriginalCurrency] = useState<string>(currencyContext.currencyData.currencySelectedISO)

    useEffect(() => {
        if (
            hotelAvailability &&
            hotelAvailability.hotelAvailabilities &&
            hotelAvailability.hotelAvailabilities.length > 0 &&
            actualStepRoomSelector > 0
        ) {
            const hotelCode = hotelAvailability.hotelAvailabilities[0]?.hotelCode ?? ''

            const regimesHotelIds =
                appConfig.config.hotels.find((h) => h.integrationCode === hotelCode)?.regimes ?? []
            const roomsHotelIds =
                appConfig.config.hotels.find((h) => h.integrationCode === hotelCode)?.rooms ?? []

       
            // en esas variables tenemos los ids de las habitaciones y los ids de los regimenes
            let avail = HotelsToAppAvailability(
                hotelAvailability,
                actualStepRoomSelector,
                onSelectRoom,
                modal,
                cartContext.data.dates.checkIn,
                cartContext.data.dates.checkOut,
                appConfig.config.rooms,
                appConfig.config.regimes,
                appConfig.config.servicesAvailable,
                selectedRoomsByIndex,
                userCurrency,
                doExchangeCurrency,
                language,
                roomsHotelIds,
                regimesHotelIds,
                setOriginalCurrency
            )

            setRoomsAvailability(avail)

        }

        // Error a nivel de hotel (p. ej. NoAvailabilityByNoContract): NoData con el mensaje específico del código.
        // Solo en búsqueda de hotel (paso > 0): en la de destino hotelAvailability puede ser de la búsqueda anterior
        const hotelErrorCode = hotelAvailability.hotelAvailabilities[0]?.errorCode
        if (hotelErrorCode && actualStepRoomSelector > 0) {
            setHotelErrorMessage(getHotelErrorMessage(hotelErrorCode))
            setShowNoDispo(true)
        }

        if (actualStepRoomSelector > roomSteps.length) {
            setActualStepBooking(3)
        } else if (actualStepRoomSelector > 0 && actualStepRoomSelector <= roomSteps.length) {
            setActualStepBooking(2)
        }
    }, [hotelAvailability, actualStepRoomSelector, currencyContext.currencyData])

    const [engineReady, setEngineReady] = useState(false);


    useEffect(() => {
        if (!engineReady) return;
        console.log("en el otro useEffect", bookingEngineContext.rooms)
        const { from, to } = bookingEngineContext.dates
        const searchCode = bookingEngineContext.searchCode
        const rooms = bookingEngineContext.rooms

        if (from && to && searchCode && rooms.length > 0) {
            cartContext.setDates({
                checkIn: from,
                checkOut: to
            })

            cartContext.updateHashCart('')
            getAvailability()
            getRoomSteps()
        }
    }, [
        bookingEngineContext.dates.from,
        bookingEngineContext.dates.to,
        bookingEngineContext.searchCode,
        bookingEngineContext.searchType,
        bookingEngineContext.rooms.length
    ])

    useEffect(() => {
        if (isThereAnyFilter()) {
            const filters = bookingEngineContext.filters
            if (filters) {
                const filtered = structuredClone(originalLocationAvailability)
                const filteredRooms = structuredClone(originalHotelAvailability)
                let filteredLocation = structuredClone(originalLocationAvailability)
                const [min, max] = filters.priceRange || []
                const minPrice = Number(min) || 0
                const maxPrice = Number(max) || Infinity

                const [minRoom, maxRoom] = filters.sizeRoomRange || []
                const minSizeRoom = Number(minRoom) || 0
                const maxRoomSize = Number(maxRoom) || Infinity

                let freeCancelation: null | boolean = null
                if (filters.freeCancel === '1') freeCancelation = true
                if (filters.freeCancel === '0') freeCancelation = false

                if (bookingEngineContext.searchType === EngineDestinationTypeLocation) {
                    filtered.locationAvailabilities = filtered.locationAvailabilities.filter(
                        ({ minimumPrice }: { minimumPrice: Price }) =>
                            minPrice <= minimumPrice.amount && minimumPrice.amount <= maxPrice
                    )

                    filtered.hotels.forEach((hotel: Hotel) => {
                        hotel.hotelStaticData?.forEach((staticData: HotelStaticData) => {
                            const finded =
                                filters.services.length > 0
                                    ? staticData?.services.some((service: Service) =>
                                        filters.services.some((fservice) => fservice === service.Code)
                                    )
                                    : true
                            if (finded) {
                                filtered.locationAvailabilities = filtered.locationAvailabilities.filter(
                                    ({ hotelCode }: { hotelCode: string }) => hotelCode !== hotel.code
                                )
                            }
                        })
                    })
                    const diferentes = [
                        ...filtered.locationAvailabilities.filter(
                            (a1: LocationAvailability) =>
                                !originalLocationAvailability.locationAvailabilities.some(
                                    (a2: LocationAvailability) => a2.hotelCode === a1.hotelCode
                                )
                        ),
                        ...originalLocationAvailability.locationAvailabilities.filter(
                            (a2: LocationAvailability) =>
                                !filtered.locationAvailabilities.some(
                                    (a1: LocationAvailability) => a1.hotelCode === a2.hotelCode
                                )
                        )
                    ]
                    filteredLocation.locationAvailabilities = diferentes
                    setLocationAvailabilityFiltered(filteredLocation)
                    setLocationAvailability({ ...filtered })
                } else {
                    let hotelsFiltered: HotelAvailabilityResponse = structuredClone(filteredRooms)
                    hotelsFiltered.hotelAvailabilities = hotelsFiltered.hotelAvailabilities.map(
                        (hotelAvailability: HotelAvailability) => ({
                            ...hotelAvailability,
                            roomAvailabilities: []
                        })
                    )
                    // CUANDO HAY HABITACIONES
                    filteredRooms.hotelAvailabilities = filteredRooms.hotelAvailabilities.map(
                        (hotelAvailability: HotelAvailability) => ({
                            ...hotelAvailability,
                            roomAvailabilities: hotelAvailability.roomAvailabilities.filter(
                                (room: RoomAvailability) => {
                                    // Con filtros activos las habitaciones con errorCode no se muestran ni cuentan en 'N elementos filtrados'
                                    if (room?.errorCode) {
                                        return false
                                    }

                                    const hotelFind: Hotel | undefined = filteredRooms.hotels.find(
                                        (hotel: Hotel | undefined) => hotel?.code === hotelAvailability?.hotelCode
                                    )

                                    const priceRange = room.rates.some((rate) =>
                                        rate.meals[0]
                                            ? rate.meals[0].price.pvpPrice.amount >= minPrice &&
                                            rate.meals[0].price.pvpPrice.amount <= maxPrice
                                            : true
                                    )

                                    const findRegimens =
                                        filters.regimens.length > 0
                                            ? room?.rates.some((rate) =>
                                                rate?.meals.some((meal) =>
                                                    filters.regimens.some((fregimen) => fregimen === meal.code)
                                                )
                                            )
                                            : true
                                    // const haveServices =
                                    //   filters.services.length > 0
                                    //     ? (hotelFind?.hotelStaticData?.some((staticData) =>
                                    //         staticData.services.some((service) =>
                                    //           filters.services.some((fservice) => fservice === service.Code)
                                    //         )
                                    //       ) ?? true)
                                    //     : true TODO

                                    const bedTypeFilter =
                                        filters.preferenceBed === ''
                                            ? true
                                            : (hotelFind?.hotelStaticData?.some((staticData) =>
                                                staticData?.rooms?.length > 0
                                                    ? staticData?.rooms.some((room) =>
                                                        room?.roomBeds?.length > 0
                                                            ? room.roomBeds.some(
                                                                (bed) =>
                                                                    bed.Type ===
                                                                    RoomBedType[
                                                                    filters.preferenceBed === '2' ? 'Double' : 'Single'
                                                                    ]
                                                            )
                                                            : true
                                                    )
                                                    : true
                                            ) ?? true)

                                    // ESTE FILTRO NO FUNCIONA, PORQUE NO EXISTE EL ROOM.ROOMBEDS, es room.beds
                                    // ademas se está comparando con el tamaño de las camas, no de la habitación
                                    const sizeRoomFiltered =
                                        hotelFind?.hotelStaticData?.some((staticData) =>
                                            staticData?.rooms?.length > 0
                                                ? staticData.rooms.some((room) =>
                                                    room?.roomBeds?.length > 0
                                                        ? room.roomBeds.some(
                                                            (bed) => bed?.width >= minSizeRoom && bed?.width <= maxRoomSize
                                                        )
                                                        : true
                                                )
                                                : true
                                        ) ?? true

                                    const haveFreeCancelation =
                                        freeCancelation !== null
                                            ? room.rates.some((rate) =>
                                                rate.meals.some((meal) => {
                                                    return meal?.cancellationPolicies.length > 0
                                                        ? meal?.cancellationPolicies?.some((policy) => {
                                                            const res = freeCancelation
                                                                ? policy?.maxMinutesForFreeCancellation > 0
                                                                : policy?.maxMinutesForFreeCancellation <= 0 ||
                                                                policy?.maxMinutesForFreeCancellation == null
                                                            return res
                                                        })
                                                        : true
                                                })
                                            )
                                            : true


                                    if (
                                        (
                                            priceRange &&
                                            findRegimens &&
                                            haveFreeCancelation &&
                                            bedTypeFilter// &&
                                            // haveServices &&
                                            //  sizeRoomFiltered
                                        )
                                    ) {
                                        hotelsFiltered.hotelAvailabilities = hotelsFiltered.hotelAvailabilities.map(
                                            (hotelAvail) => ({
                                                ...hotelAvail,
                                                roomAvailabilities: [...hotelAvail.roomAvailabilities, room]
                                            })
                                        )

                                    }

                                    return (
                                        priceRange &&
                                        findRegimens &&
                                        haveFreeCancelation &&
                                        bedTypeFilter// &&
                                        // haveServices &&
                                        //sizeRoomFiltered
                                    )
                                }
                            )
                        })
                    )
                    const hotelCode = originalHotelAvailability.hotelAvailabilities[0]?.hotelCode ?? ''

                    const regimesHotelIds =
                        appConfig.config.hotels.find((h) => h.integrationCode === hotelCode)?.regimes ?? []
                    const roomsHotelIds =
                        appConfig.config.hotels.find((h) => h.integrationCode === hotelCode)?.rooms ?? []
                    console.log(hotelsFiltered)
                    let avail = HotelsToAppAvailability(
                        hotelsFiltered,
                        actualStepRoomSelector,
                        onSelectRoom,
                        modal,
                        cartContext.data.dates.checkIn,
                        cartContext.data.dates.checkOut,
                        appConfig.config.rooms,
                        appConfig.config.regimes,
                        appConfig.config.servicesAvailable,
                        selectedRoomsByIndex,
                        userCurrency,
                        doExchangeCurrency,
                        language,
                        roomsHotelIds,
                        regimesHotelIds,
                        setOriginalCurrency
                    )

                    setRoomsAvailabilityFiltered(avail)
                    setHotelAvailability({ ...filteredRooms })
                }
            }
        } else if (availabilityType === EngineDestinationTypeHotel) {
            // Sin filtros: el filtrado anterior sobrescribió hotelAvailability con la copia filtrada.
            // Se restaura la original para que vuelvan todas las habitaciones, también las deshabilitadas por errorCode
            setHotelAvailability(originalHotelAvailability)
            setRoomsAvailabilityFiltered([])
        }

    }, [bookingEngineContext.filters])
    const filteredCount = roomsAvailabilityFiltered.length


    const [defaultLabel, setDefaultLabel] = useState<string>('')
    const onSetDefaultLabel = () => {
        var lastLabel = sessionStorage.getItem('lastLabel')
        setDefaultLabel(lastLabel ?? '')
    }

    const getPaymentsMethods = () => {
        GetPaymentsMethods().then((r) => {
            if (r?.type == 1 && r.data) {
                setPaymentsMethods(r.data as PaymentMethodData[])
            }
        })
    }

    useEffect(() => {
        getUserHistory()
        getAvilableDestinations()
        onSetDefaultLabel()
        getPaymentsMethods()
    }, [])

    const isThereAnyFilter = () => {
        const f = bookingEngineContext.filters
        if (!f) return false
        return (
            (f.priceRange && (
                f.priceRange[0] != null ||
                f.priceRange[1] != null
            )) ||
            (f.sizeRoomRange && (
                (Number(f.sizeRoomRange?.[0] ?? 0) > 0) ||
                (Number(f.sizeRoomRange?.[1] ?? Infinity) < Infinity)
            )) ||
            (f.regimens && f.regimens.length > 0) ||
            (f.services && f.services.length > 0) ||
            (f.preferenceBed && f.preferenceBed !== '') ||
            (f.freeCancel !== null && f.freeCancel !== '')
        )
    }

    const hasFilters = isThereAnyFilter()
    const roomsToShow = hasFilters ? roomsAvailabilityFiltered : roomsAvailability


    return (
        <div className="booking-engine-page">
            <Modal
                closable
                title="Simulador de pago"
                open={isSimulateBookingPaymentOpened}
                onCancel={toogleSimulateBookingPayment}
                footer={(_, { CancelBtn }) => (
                    <>
                        <Button
                            className="app-button danger"
                            danger
                            onClick={() => {
                                //Go to customer voucher
                                toogleSimulateBookingPayment()
                                navigate(GetFormattedUrl(`private/payment-ko`))
                            }}>
                            Simular KO
                        </Button>
                        <Button
                            className="app-button"
                            onClick={() => {
                                //Clear engine search
                                bookingEngineContext.clearContext()
                                //Clear cart
                                cartContext.clearCart()
                                //Go to customer voucher
                                navigate(GetFormattedUrl(`private/payment-ok-customer-voucher/123`))
                            }}>
                            Simular OK
                        </Button>
                        <CancelBtn />
                    </>
                )}>
                <p>Por favor, pulse una de las siguientes opciones para simular el flujo del pago</p>
            </Modal>

            <div className="widget-container">
                <BookingUpdateContext />
                <EngineWidget
                    userHistory={userHistory}
                    onGetCoincidences={getSearchCoincidences}
                    searchCoincidences={searchCoincidences}
                    availableDestinations={availableDestinations}
                    cleanForm={cleanForm}
                />
            </div>

            <div className="booking-container">
                {isGettingAvailability ? (
                    <LoadingPage />
                ) : actualStepBooking === 3 ||
                    (locationAvailability && locationAvailability.hotels?.length > 0) ||
                    (roomsAvailability && roomsAvailability.length > 0) ? (
                    <>
                        <div className="booking-engine-pagination">
                            <Pagination actualStep={actualStepBooking} onChangeStep={onChangeStep} />
                        </div>
                        <div className="booking-engine-steps">
                            {actualStepBooking === 2 && actualStepRoomSelector > 0 && roomSteps.length > 1 ? (
                                <EngineSteps
                                    actualStep={actualStepRoomSelector}
                                    occupancy={roomSteps}
                                    onStepChange={(step: number) => {
                                        setActualStepRoomSelector(step)
                                    }}
                                />
                            ) : (
                                <></>
                            )}
                        </div>
                        {isThereAnyFilter() && (
                            (locationAvailabilityFiltered.locationAvailabilities.length > 0 ||
                                roomsAvailabilityFiltered.length > 0) ? (
                                <h4 style={{ color: 'blue' }}>
                                    {t('filter.elements-filtered', {
                                        count: roomsAvailabilityFiltered.length
                                    })}
                                </h4>
                            ) : null
                        )}


                        <div className={`booking-engine-result`}>
                            <Row gutter={16}>
                                <Col
                                    xs={
                                        appConfig.device === 'mobile' ||
                                            availabilityType === EngineDestinationTypeLocation
                                            ? 24
                                            : 18
                                    }>
                                    {
                                        availabilityType === EngineDestinationTypeLocation ? (
                                            LocationToAppAvailability(
                                                locationAvailability,
                                                bookingEngineContext,
                                                modal,
                                                appConfig.config.hotels,
                                                userCurrency,
                                                doExchangeCurrency,
                                                appConfig.config.servicesAvailable,
                                                language,
                                                client
                                            ) // Mostraoms la disponibilidad de localización, es el paso 2
                                        ) : actualStepRoomSelector > 0 && actualStepRoomSelector <= roomSteps.length ? (
                                            <RoomsAvailabilityComponent avail={roomsToShow} /> // Mostramos la disponibilidad de hoteles, es el paso 2
                                        ) : (
                                            <BookingData
                                                countries={countries}
                                                paymentsMethods={paymentsMethods}
                                                availData={roomsToShow}
                                                ref={bookingDataFormRef} // Pasamos la referencia a BookingDataForm
                                                originalCurrency={originalCurrency}
                                                doExchangeCurrency={doExchangeCurrency}
                                                onFormValidityChange={setIsBookingFormValid}
                                                setBookingId={setBookingId}

                                            />
                                        ) //Mostramos el formulario, es el paso 3
                                    }

                                </Col>
                                <Col
                                    className={`cart ${appConfig.device} ${isCartVisible ? 'visible' : 'hidden'}`}
                                    xs={availabilityType === EngineDestinationTypeLocation ? 0 : 6}>
                                    <Cart
                                        scrollToPayButton={scrollToPayButton}
                                        onClickInPayButton={toogleSimulateBookingPayment}
                                        hotelData={hotelUtils.hotel}
                                        from={bookingEngineContext.dates.from}
                                        to={bookingEngineContext.dates.to}
                                        notifyRemoveRoom={onRemoveRoomFromCart}
                                        postponedAvailable={isBookingFormValid}
                                        bookingId={bookingId}
                                        postponedBooking={async () => {
                                            if (bookingDataFormRef.current?.createPostponedBooking) {
                                                return await bookingDataFormRef.current.createPostponedBooking();
                                            }
                                            return null;
                                        }}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </>
                ) : (
                    <div className="alert-search-availability">
                        {(showNoDispo) ? (
                            <>

                                <NoData message={hotelErrorMessage || t('pages.bookings-page.no-availability.description')!} />
                            </>
                        ) : (
                            // <Alert message={t('pages.bookings-page.search-availability.title')} description={t('pages.bookings-page.search-availability.description')} type="info" showIcon />

                            <div className="container-finder-icons-message">

                                <div className="container-flexs">
                                    <div className="transparent" />
                                    <div className="flex">
                                        <HomeMinimalist className="home-minimalist-icon" />
                                        <HomeMinimalist className="home-minimalist-icon" />
                                        <HomeMinimalist className="home-minimalist-icon" />
                                    </div>
                                    <div className="position-down-icon">
                                        <HomeMinimalist
                                            color="var(--primary-app-color)"
                                            className="home-minimalist-icon"
                                        />
                                        <FinderIcon className="position-down-icon-search" />
                                    </div>
                                    <div className="flex">
                                        <HomeMinimalist className="home-minimalist-icon" />
                                        <HomeMinimalist className="home-minimalist-icon" />
                                        <HomeMinimalist className="home-minimalist-icon" />
                                    </div>
                                </div>

                                {!isThereAnyFilter() ? (
                                    <p>{t('pages.bookings-page.search-availability.description')}</p>
                                ) : (
                                    <p style={{ color: 'var(--primary-app-color)' }}>
                                        {t('filter.no-results')}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/*    
            <div className="postponed-bookings-container">
                <PostponedBookings list={postponedBookings}/>
            </div> 
            */}
        </div>
    )
}

export default BookingEnginePage

const RoomsAvailabilityComponent = ({ avail }: { avail: any[] }): JSX.Element => {
    const [availData, setAvailData] = useState<JSX.Element[]>([])

    useEffect(() => {
        setAvailData([])
        setTimeout(() => {
            setAvailData(avail)
        }, 0)
    }, [avail])

    return <>{availData}</>
}

const BookingUpdateContext = (): JSX.Element => {
    const appConfig = useAppConfig()

    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)

    const bookingEngineContext = useBookingEngineContext()

    const [hotelCodeFromUrl] = useState<string>(urlParams.get('hc') ?? '')
    useEffect(() => {
        if (hotelCodeFromUrl) {
            var hotelData = appConfig.config.hotels.find((h) => h.code === hotelCodeFromUrl)
            // En la URL viene el code del hotel; para la disponibilidad se guarda su integrationCode
            const integrationCode = hotelData?.integrationCode || hotelCodeFromUrl
            sessionStorage.setItem('lastSearchCode', integrationCode)
            sessionStorage.setItem('lastSearchType', '0')
            bookingEngineContext.updateContext({ label: hotelData?.name ?? '' })
        }
    }, [hotelCodeFromUrl])

    const [cityCodeFromUrl] = useState<string>(urlParams.get('cc') ?? '')
    useEffect(() => {
        if (cityCodeFromUrl) {
            var hotelData = appConfig.config.hotels.find((h) => h.cityCode === cityCodeFromUrl)
            sessionStorage.setItem('lastSearchCode', cityCodeFromUrl)
            sessionStorage.setItem('lastSearchType', '1')
            bookingEngineContext.updateContext({ label: hotelData?.city ?? '' })
        }
    }, [cityCodeFromUrl])

    const [dateFromFromUrl] = useState<string>(urlParams.get('df') ?? '')
    useEffect(() => {
        if (dateFromFromUrl !== '' && dateFromFromUrl !== 'Invalid date') {
            sessionStorage.setItem('lastFrom', dayjs(dateFromFromUrl).format('YYYY-MM-DDTHH:mm:ss.sssZ'))
        }
    }, [dateFromFromUrl])

    const [dateToFromUrl] = useState<string>(urlParams.get('dt') ?? '')
    useEffect(() => {
        if (dateToFromUrl !== '' && dateFromFromUrl !== 'Invalid date') {
            sessionStorage.setItem('lastTo', dayjs(dateToFromUrl).format('YYYY-MM-DDTHH:mm:ss.sssZ'))
        }
    }, [dateToFromUrl])

    return <div></div>
}